import React from 'react';
import {
  TranslationContext,
  AnalyticsContext,
  ErrorLoggingContext
} from '@strava/container-context';
import { logError } from '../../utils/sentry';
import I18n from '../../utils/I18n';
import styles from './styles.scss';

// We want to track each url that we use to load a remote entry,
// because we want to check if we've already downloaded and avoid
// downloading the same remote multiple times.
const remoteUrls = {};

// We want to initialize the 'scope' (remote entry code) a single time,
// and not many times for many different components.
// Specifically, we only want to call this line once:
//    `containerResolve(window[scope].init(legacyShareScope));`
const initializedScopes = {};

const cleanup = (scriptElement) => {
  if (scriptElement.parentNode) {
    scriptElement.parentNode.removeChild(scriptElement);
  }
};

const loadDynamicScript = (url) => {
  if (remoteUrls[url] == null) {
    remoteUrls[url] = new Promise((resolve, reject) => {
      // insert remote script into document and add
      // onerror and onload listeners that will finalize
      // installation
      const element = document.createElement('script');
      element.src = url;
      element.type = 'text/javascript';
      element.async = true;

      element.onload = () => {
        console.log(`Dynamic Script Loaded: ${url}`);
        resolve(element);
      };

      element.onerror = () => {
        console.error(`Dynamic Script Error: ${url}`);
        reject(element);
      };

      document.head.appendChild(element);
    });
  }

  return remoteUrls[url];
};

const cleanupScript = (url) => {
  if (remoteUrls[url]) {
    remoteUrls[url].then(cleanup, cleanup);
  }
};

// TODO: remove once we've upgraded to webpacker 6 (webpack 5)
// The code here only supports consumption of federated modules
// and does not allow hosting federated modules
// copied from
// https://github.com/module-federation/practical-module-federation/blob/master/chapter7/packages/home-wp4/src/App.jsx
const useDynamicScript = (url) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    if (!url) {
      return;
    }

    const promise = loadDynamicScript(url);

    setIsLoading(true);
    setFailed(false);

    promise.then(
      () => {
        console.log(`Dynamic Script Loaded: ${url}`);
        setIsLoading(false);
      },
      () => {
        console.error(`Dynamic Script Error: ${url}`);
        setIsLoading(false);
        setFailed(true);
      }
    );

    // eslint-disable-next-line consistent-return
    return () => {
      // cleanup code to remove script added as part of loading
      // the remote federated module. This code is ran before
      // another execution of this useEffect is ran before
      // rendering and when the component unmounts
      console.log(`Dynamic Script Removed: ${url}`);
      cleanupScript(url);
    };
  }, [url]);

  return {
    isLoading,
    failed
  };
};

// eslint-disable-next-line react/prop-types
const DynamicWidget = ({ url, scope, component, appContext }) => {
  const { isLoading, failed } = useDynamicScript(url);

  if (isLoading) {
    return null;
  }

  if (failed) {
    return <h2>Failed to load dynamic script: {url}</h2>;
  }

  const Component = React.lazy(
    () =>
      // moduleResolve is the reference to use to mark this
      // promise as completed/resolved.
      new Promise((moduleResolve) => {
        // signal webpacker to load the pages react
        // and create a share scope to initialize with
        // the component.
        // eslint-disable-next-line global-require
        const react = require('react');

        // eslint-disable-next-line global-require
        const translationscontext = require('@strava/container-context');

        const legacyShareScope = {
          react: {
            [react.version]: {
              get: () => {
                return () => react;
              },
              singleton: true,
              loaded: true,
              from: 'webpack4'
            }
          },
          // the context provider needs to be a singleton for the
          // shared info to be the same between container and MFE
          '@strava/container-context': {
            [translationscontext.version]: {
              get: () => {
                return () => translationscontext;
              },
              singleton: true,
              loaded: true,
              from: 'webpack4'
            }
          }
        };
        new Promise((containerResolve) => {
          // when we load the remoteEntry.js, it creates the scope
          // defined in the remote exposed components.  We use the
          // pages shared scope to compare the react version on the
          // page vs the one of the remote component

          // Initialize a remote once even when multiple components use it
          if (initializedScopes[scope]) {
            containerResolve();
          } else {
            initializedScopes[scope] = true;
            containerResolve(window[scope].init(legacyShareScope));
          }
        }).then(() => {
          // finally, load the component/module and resolve
          // topmost promise
          window[scope].get(component).then((factory) => {
            moduleResolve(factory());
          });
        });
      })
  );

  return (
    <React.Suspense fallback="">
      <Component appContext={appContext} />
    </React.Suspense>
  );
};

// eslint-disable-next-line no-unused-vars,react/prop-types
const Microfrontend = ({ url, scope, component, appContext }) => (
  <div className={styles.mfcontainer}>
    <AnalyticsContext.Provider
      value={{
        // Other places we declare and refer to the trackv2 function:
        //   app/views/common/_external_analytics.haml
        //   app/javascript/utils/analytics.js
        // The function accepts these arguments:
        //  category
        //  page
        //  action
        //  element
        //  properties
        track: (data) => {
          return window.Strava.ExternalAnalytics.trackV2(data);
        }
      }}
    >
      <TranslationContext.Provider
        value={{
          t: (key, props = {}) => {
            return I18n.t(key, props);
          },
          getLanguage: () => {
            return I18n.language();
          }
        }}
      >
        <ErrorLoggingContext.Provider value={{ logError }}>
          <DynamicWidget
            url={url}
            scope={scope}
            component={component}
            appContext={appContext}
          />
        </ErrorLoggingContext.Provider>
      </TranslationContext.Provider>
    </AnalyticsContext.Provider>
  </div>
);

export default Microfrontend;
